/*
 * Conventional values of time length in ms
 * */
import {
  SYSTEM_ID_ENERGY_DEMO,
  SYSTEM_ID_ENERGY_DEMO_UK,
  SYSTEM_ID_ENERGY_US_DEMO_1,
  SYSTEM_ID_ENERGY_US_DEMO_2,
  SYSTEM_ID_ENERGY_US_DEMO_3,
  SYSTEM_ID_PERCIVAL,
} from '@twaice-fe/shared/constants';

export enum TimeLength {
  ZERO = 0,
  SECOND = 1000,
  HALF_MINUTE = 30000,
  MINUTE = 60000,
  QUARTER_HOUR = 900000,
  HOUR = 3600000,
  QUARTER_DAY = 21600000,
  HALF_DAY = 43200000,
  DAY = 86400000,
  WEEK = 604800000,
}

export enum TimeInterval {
  ZERO = 0,
  SECOND = 1000,
  HALF_MINUTE = 30000,
  MINUTE = 60000,
  QUARTER_HOUR = 900000,
  HOUR = 3600000,
  QUARTER_DAY = 21600000,
  HALF_DAY = 43200000,
  DAY = 86400000,
  SEVEN_DAYS = 604800000,
  THIRTY_DAYS = 2592000000,
  //eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
  ONE_WEEK = 604800000,
  THREE_MONTHS = 7890000000,
  SIX_MONTHS = 15780000000,
  NINE_MONTHS = 23670000000,
  ONE_YEAR = 31536000000,
  THREE_YEARS = 31536000000 * 3,
}

export const getCurrentDateBasedOnSystemIDInStringFormat = (systemID: string): string | null => {
  switch (systemID) {
    // Workaround for Sembcorp
    case SYSTEM_ID_PERCIVAL:
      return '2022-10-20T00:00:00';

    // Workaround for energy demo (Birch)
    case SYSTEM_ID_ENERGY_DEMO:
      return '2022-09-01T00:00:00';

    // Workaround for energy demo uk (Oak)
    case SYSTEM_ID_ENERGY_DEMO_UK:
      return '2023-12-01T00:00:00+00:00';

    // Workaround for energy demo us-1 (Elm)
    case SYSTEM_ID_ENERGY_US_DEMO_1:
      return '2024-04-01T00:00:00+00:00';
    // Workaround for energy demo us-2 (Cactus)
    case SYSTEM_ID_ENERGY_US_DEMO_2:
      return '2024-10-01T00:00:00+00:00';
    // Workaround for energy demo us-3 (Redwood)
    case SYSTEM_ID_ENERGY_US_DEMO_3:
      return '2024-04-09T00:00:00+00:00';

    default:
      return null;
  }
};
export const getTodayBasedOnSystemID = (systemID: string): number => {
  const currentDateBasedOnSystemIDInStringFormat = getCurrentDateBasedOnSystemIDInStringFormat(systemID);
  return currentDateBasedOnSystemIDInStringFormat ? Date.parse(currentDateBasedOnSystemIDInStringFormat) : Date.now();
};

export const getTimeRangeBasedOnSystemIDInUnixTimestamp = (
  systemID: string,
  timeResolution: TimeInterval
): { startDate: number; endDate: number } => {
  const today = getTodayBasedOnSystemID(systemID);

  return {
    startDate: today - timeResolution,
    endDate: today,
  };
};

export const getCurrentDateBasedOnSystemID = (systemID: string): Date => {
  const currentDateBasedOnSystemIDInStringFormat = getCurrentDateBasedOnSystemIDInStringFormat(systemID);
  const todayMidnight = new Date();
  todayMidnight.setUTCHours(0, 0, 0, 0);
  return currentDateBasedOnSystemIDInStringFormat ? new Date(currentDateBasedOnSystemIDInStringFormat) : todayMidnight;
};

export const getUnixTimestampFromDate = (date: Date): number => Math.floor(date.getTime() / 1000);

// Values are the range ids used throughout the app: TimeRangeInterface.id, the
// `timeRange` query param, and mapTimeRangeIdToDE. Identifying a range by id rather
// than by its position keeps a default written by one page from selecting a
// different range on a page whose list has a different shape.
export enum TimeRangeEnum {
  LAST_SEVEN_DAYS = 'sevenDays',
  LAST_THIRTY_DAYS = 'thirtyDays',
  LAST_SIXTY_DAYS = 'sixtyDays',
  LAST_NINETY_DAYS = 'ninetyDays',
  LAST_THREE_HUNDRED_SIXTY_FIVE_DAYS = 'threeHundredSixtyFiveDays',
}

export enum TimeRangeEnumDE {
  LAST_HOUR = 'lastHour',
  TODAY = 'today',
  YESTERDAY = 'yesterday',
  LAST_24_HOURS = 'last24Hours',
  LAST_7_DAYS = 'last7Days',
  LAST_30_DAYS = 'last30Days',
  THIS_MONTH = 'thisMonth',
  PREVIOUS_MONTH = 'previousMonth',
}

export const areDatesEqual = (date1: Date, date2: Date): boolean =>
  date1.getFullYear() === date2.getFullYear() && date1.getMonth() === date2.getMonth() && date1.getDate() === date2.getDate();

export const substractDays = (date: Date, days: number): Date => {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() - days);
  return newDate;
};

export const addDays = (date: Date, days: number): Date => {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() + days);
  return newDate;
};
